/**
 * Batch 2 PR 2 · WorkOS AuthKit React wiring.
 *
 * • Wraps the entire app in <AuthKitProvider> so `useAuth()` is available
 *   anywhere in the tree.
 * • A small effect bridges the AuthKit `getAccessToken` into the axios
 *   interceptor at module scope (see lib/api.js) — this keeps the JWT
 *   attachment logic out of every component that talks to the backend.
 * • Public routes (`/`, `/artifacts/view/:slug`) render regardless of auth
 *   state. `/command` renders the existing Shell unconditionally if the
 *   user is signed in, otherwise calls `signIn()` to bounce through the
 *   WorkOS hosted sign-in page.
 *
 * AuthKit client ID + redirect URI are sourced from env. Until the user
 * fills REACT_APP_WORKOS_CLIENT_ID, the provider is rendered without a
 * clientId prop — in that mode the SDK no-ops and `useAuth()` returns
 * `{ user: null, isLoading: false }`. Combined with the backend's
 * PROCUREO_AUTH_BYPASS dev flag, this keeps local dev unblocked while the
 * WorkOS Dashboard is being configured.
 */
// ─── Observability · Wave 2 Bucket 2 PR 1 ──────────────────────────────────
// Sentry init must run BEFORE any other application code so unhandled
// errors during React bootstrapping are captured. `initSentry()` is a
// no-op when REACT_APP_SENTRY_DSN is empty/unset (local dev + CI).
import { initSentry, bindAuthContext as bindSentryAuth } from "./lib/observability";
import { useEffect } from "react";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import "@/App.css";

import { Toaster } from "@/components/ui/sonner";
import Shell from "./components/Shell";
import Landing from "./pages/Landing";
import { setAccessTokenGetter } from "./lib/api";
import { loadRbacMatrix } from "./lib/rbac";

// Side-effect call · runs at module load before any React component mounts.
// In ES modules, imports above are hoisted and executed before this line,
// so all the SDK code Sentry depends on is loaded by the time init runs.
initSentry();

const WORKOS_CLIENT_ID = process.env.REACT_APP_WORKOS_CLIENT_ID || "";
const WORKOS_REDIRECT_URI =
  process.env.REACT_APP_WORKOS_REDIRECT_URI ||
  (typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : "");

// ─── Bridges AuthKit's getAccessToken into axios ─────────────────────────
function AccessTokenBridge() {
  const { getAccessToken, isLoading, user } = useAuth();
  useEffect(() => {
    if (isLoading) return;
    setAccessTokenGetter(getAccessToken || null);
    // PR 5.5 · once axios has the token bridge, pull the canonical RBAC
    // matrix from the server.  Fail-closed by design — if this 401s or
    // network-errors, every `can()` returns false until a successful
    // retry (next page nav re-runs the effect).
    loadRbacMatrix();
    // Wave 2 Bucket 2 PR 1 · bind authenticated user identity onto the
    // Sentry scope so subsequent errors carry user_id + (eventually)
    // tenant_id tags. Tenant_id is sourced server-side by the backend's
    // /api/auth/whoami; on the frontend we have user.id immediately and
    // populate tenant_id from the whoami response when other components
    // fetch it. No-op when Sentry DSN is unset.
    if (user && user.id) {
      bindSentryAuth({ userId: user.id });
    }
    return () => setAccessTokenGetter(null);
  }, [getAccessToken, isLoading, user]);
  return null;
}

// ─── Protected route gate ────────────────────────────────────────────────
// If the user is unauthenticated, kick them to WorkOS hosted sign-in.
// AuthKit React's signIn() handles the redirect + returns to the current
// URL after callback completes. While AuthKit is loading the initial
// session, render the existing Shell-shaped skeleton (Shell already
// handles its own load states).
function ProtectedRoute({ children }) {
  const { user, isLoading, signIn } = useAuth();
  useEffect(() => {
    if (isLoading) return;
    if (!user && typeof signIn === "function") {
      signIn();
    }
  }, [user, isLoading, signIn]);
  if (isLoading || !user) {
    return (
      <div
        data-testid="auth-loading"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#020617",
          color: "#94a3b8",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        {isLoading ? "Verifying session…" : "Redirecting to sign-in…"}
      </div>
    );
  }
  return children;
}

// AuthCallback · the landing point WorkOS redirects to after sign-in.
// The AuthKit React SDK auto-exchanges the `code` + `state` query params for
// a session as soon as `AuthKitProvider` mounts (which it always does, on
// every route, because it wraps `<AppRoutes />`). This component's only job
// is to wait for that exchange to complete, then send the user to /command.
// Without this route the user lands on a blank page (no Route matched
// `/auth/callback`, React Router rendered nothing). Same shape as the
// ProtectedRoute loading screen for visual continuity.
function AuthCallback() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (isLoading) return;
    // Once the SDK has finished exchanging the code, hop to /command.
    // ProtectedRoute will gate access if the exchange somehow failed.
    navigate("/command", { replace: true });
  }, [user, isLoading, navigate]);
  return (
    <div
      data-testid="auth-callback-loading"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#020617",
        color: "#94a3b8",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}
    >
      Finalising sign-in…
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/command"
        element={
          <ProtectedRoute>
            <Shell />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // AuthKitProvider requires a clientId; when it's not set yet (e.g., before
  // the WorkOS Dashboard is configured), we render the public routes only.
  // /command is intentionally rendered without ProtectedRoute in this branch
  // so a pre-WorkOS environment can boot the operator surface; this is a
  // development-time convenience only — production builds always set
  // REACT_APP_WORKOS_CLIENT_ID and take the AuthKitProvider branch below.
  if (!WORKOS_CLIENT_ID) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/command" element={<Shell />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AuthKitProvider
          clientId={WORKOS_CLIENT_ID}
          redirectUri={WORKOS_REDIRECT_URI}
        >
          <AccessTokenBridge />
          <AppRoutes />
        </AuthKitProvider>
      </BrowserRouter>
      <Toaster theme="dark" />
    </div>
  );
}

export default App;
